import { BadgeCheck, ChevronsDownUp, GitCompareArrows, Shuffle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { Followup, FollowupKind } from "./types";

interface FollowupChipsProps {
  items: (Followup | string)[];
  onPick: (question: string) => void;
  /** "sticky" floats fixed to the bottom of the surface with a fade-up gradient.
   *  "grid" lays the chips out as a responsive 2-col → 4-col grid with no
   *  section label — used by the empty-state starter chips so the rail reads
   *  as a tap-to-start row rather than a follow-up suggestion. */
  variant?: "inline" | "sticky" | "grid";
}

// D16 — locked chip taxonomy: drill / compare / pivot / verify, in that order.
const KIND_META: Record<FollowupKind, { label: string; Icon: LucideIcon; tone: string }> = {
  drill: { label: "Drill down", Icon: ChevronsDownUp, tone: "text-status-info" },
  compare: { label: "Compare", Icon: GitCompareArrows, tone: "text-status-warning" },
  pivot: { label: "Pivot", Icon: Shuffle, tone: "text-primary" },
  verify: { label: "Verify", Icon: BadgeCheck, tone: "text-status-success" },
};

// Stable display order regardless of the order the model emitted them in.
const KIND_ORDER: FollowupKind[] = ["drill", "compare", "pivot", "verify"];

function asFollowup(f: Followup | string): Followup {
  if (typeof f === "string") return { text: f, kind: "drill" };
  // Object followups may arrive with an out-of-contract kind (legacy spec /
  // model slip). Coerce unknown kinds to "drill" so they render with a real
  // icon and sort in a stable slot instead of landing at index -1.
  return KIND_ORDER.includes(f.kind) ? f : { ...f, kind: "drill" };
}

/**
 * Typed follow-up chips. The agent emits exactly 4 — one drill, one compare,
 * one pivot, one verify — each with a lucide icon + faint kind tag. Click →
 * fires onPick, which the surface uses to start a new dashboard turn.
 *
 * Sticky variant floats fixed to the bottom of the surface with a fade-up
 * gradient above it; inline variant sits at the bottom of the dashboard card
 * (used for PDF capture).
 */
export function FollowupChips({ items, onPick, variant = "inline" }: FollowupChipsProps) {
  if (!items || items.length === 0) return null;

  const chips = items
    .map(asFollowup)
    .slice()
    .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));

  // All variants use the 4-col grid on desktop so follow-up chips match
  // starter chips visually. Mobile collapses to 2 columns. (User feedback
  // 2026-05-31: "chips should be matched and aligned horizontally".)
  const listClassName =
    "grid grid-cols-2 gap-1.5 sm:grid-cols-4 sm:gap-2";

  const list = (
    <ul role="list" className={listClassName}>
      {chips.map((f, i) => {
        const meta = KIND_META[f.kind] ?? KIND_META.drill;
        const Icon = meta.Icon;
        return (
          <li key={i} role="listitem">
            <button
              type="button"
              onClick={() => onPick(f.text)}
              className="group inline-flex h-full min-h-[64px] w-full items-start gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-left text-sm text-foreground/90 shadow-sm transition-all duration-200 ease-out hover:bg-accent hover:text-foreground hover:shadow-md motion-safe:hover:-translate-y-0.5 motion-safe:hover:scale-[1.02]"
            >
              <span className={cn("mt-0.5 shrink-0", meta.tone)}>
                <Icon className="h-3.5 w-3.5" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {meta.label}
                </span>
                <span className="text-foreground/90 leading-snug">{f.text}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  if (variant === "sticky") {
    return (
      <div className="pointer-events-none sticky bottom-0 left-0 right-0 mt-2 pb-3 pt-3">
        <div className="absolute inset-x-0 bottom-0 top-0 -z-10 bg-gradient-to-t from-background via-background/95 to-transparent" />
        <nav
          aria-label="Suggested follow-up questions"
          className="pointer-events-auto mx-auto max-w-5xl px-2"
        >
          <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">
            Follow up
          </div>
          {list}
        </nav>
      </div>
    );
  }

  if (variant === "grid") {
    // No "Follow up" label — in the empty state these ARE the starting point,
    // not a follow-up suggestion.
    return (
      <nav aria-label="Starter questions">{list}</nav>
    );
  }

  return (
    <nav aria-label="Suggested follow-up questions" className="pt-2">
      {/* The "Follow up" label nudges on the same canonical cadence as the
          See more / See less chevrons so the three affordances read alike. */}
      <div className="mb-2 inline-block text-[11px] uppercase tracking-wider text-muted-foreground animate-nudge motion-reduce:animate-none">
        Follow up
      </div>
      {list}
    </nav>
  );
}
